import React, { useEffect, useMemo, useState } from 'react';

import { Autocomplete, CircularProgress, TextField } from '@mui/material';

import { TourOption, useGetTourOptions } from '~/hooks/apis/tours/useTourSearch';

interface Props {
  value: TourOption | null;
  onChange: (tour: TourOption | null) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
}

const SEARCH_DEBOUNCE_MS = 300;

export default function TourPicker({ value, onChange, label = 'Tour', helperText, disabled }: Props) {
  const [inputValue, setInputValue] = useState('');
  const [searchString, setSearchString] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setSearchString(inputValue), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [inputValue]);

  const { data: options = [], isFetching } = useGetTourOptions(searchString);

  // Kept in the list so the field does not clear itself as the search changes.
  const mergedOptions = useMemo(() => {
    if (!value || options.some((option) => option.id === value.id)) {
      return options;
    }
    return [value, ...options];
  }, [options, value]);

  return (
    <Autocomplete
      value={value}
      onChange={(_event, tour) => onChange(tour)}
      inputValue={inputValue}
      onInputChange={(_event, next) => setInputValue(next)}
      options={mergedOptions}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      loading={isFetching}
      disabled={disabled}
      filterOptions={(all) => all}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
